import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { flushSync } from 'react-dom';
import { Alert, PageHeader, StatusPill, DatePicker } from '../ui';
import SelectMenu from '../ui/SelectMenu';
import DownloadDropdown from './DownloadDropdown';
import ReportTitleSwitcher from './ReportTitleSwitcher';
import { invoiceAPI, quoteAPI, jobAPI, unwrapList } from '../../services/api';
import { REPORT_DEFINITIONS, REPORT_SLICERS } from './reportDefinitions';
import ReportChart from './ReportChart';
import { parseDate } from '../../utils/formatDate';
import './ReportDetail.print.css';
import { dealerToday } from '../../utils/dealerDate';
import { hasDateRange, reportDayOf, inRange, monthKeyOf, monthLabel } from '../../utils/reportRange';

function formatValue(value, format) {
  if (value == null || value === '—') return '—';
  switch (format) {
    case 'currency':
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value / 100);
    case 'percent':
      return `${value}%`;
    case 'percent_flagged':
      // Flags values below 5% threshold with warning color
      const isBelowThreshold = typeof value === 'number' && value < 5;
      return (
        <span style={{ color: isBelowThreshold ? 'var(--color-status-error-text)' : undefined }}>
          {value}%{isBelowThreshold ? ' (!)' : ''}
        </span>
      );
    case 'text':
      return value;
    case 'date':
      if (!value) return '—';
      // IDI-518: bare YYYY-MM-DD (paidDate) reads as that local day.
      return (parseDate(value) || new Date(value)).toLocaleDateString();
    case 'status':
      return <StatusPill status={value} />;
    case 'number':
      return typeof value === 'number' ? value.toLocaleString() : value;
    case 'bar':
      const pct = typeof value === 'number' ? Math.min(100, Math.max(0, value)) : 0;
      return (
        <div className="w-20 h-2 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--neutral-200)' }}>
          <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: 'var(--color-action-primary)' }} />
        </div>
      );
    default:
      return value;
  }
}

function KpiCard({ label, value, format }) {
  return (
    <div className="card p-4">
      <p className="text-xs uppercase tracking-wider mb-1" style={{ color: 'var(--color-text-secondary)' }}>{label}</p>
      <p className="text-2xl font-semibold" style={{ fontFamily: 'var(--font-display)', color: 'var(--color-text-primary)' }}>
        {formatValue(value, format)}
      </p>
    </div>
  );
}

const dataSourceMap = {
  invoices: (params) => invoiceAPI.getInvoices(params),
  quotes: (params) => quoteAPI.getQuotes(params),
  jobs: (params) => jobAPI.getJobs(params),
};

// Single "Download ▾" dropdown per LAT-17 — extracted to ./DownloadDropdown.js for reuse.

export default function ReportDetail({ reportConfig: initialConfig, onBack, onSwitchReport, allReports, period, filter }) {
  const [reportConfig, setReportConfig] = useState(initialConfig);

  // Keep local reportConfig in sync when the parent switches to a different report
  // (e.g. via the title-chevron switcher). Without this, the header shows the new
  // title only after a full remount.
  // IDI-797 D3: a new report opens on page 1 (filters, sort and tab are kept).
  useEffect(() => {
    if (initialConfig && initialConfig.id !== reportConfig?.id) {
      setReportConfig(initialConfig);
      setCurrentPage(1);
    }
  }, [initialConfig, reportConfig]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rawData, setRawData] = useState([]);
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [filters, setFilters] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [activeSlicer, setActiveSlicer] = useState('By Month');
  // IDI-797 D5: while the browser prints, the table holds every row.
  const [printAll, setPrintAll] = useState(false);
  const pageSize = 20;

  useEffect(() => {
    // The browser lays out for print as soon as beforeprint returns, so the
    // rows must be committed synchronously (React 18 batches otherwise).
    const onBeforePrint = () => flushSync(() => setPrintAll(true));
    const onAfterPrint = () => setPrintAll(false);
    window.addEventListener('beforeprint', onBeforePrint);
    window.addEventListener('afterprint', onAfterPrint);
    return () => {
      window.removeEventListener('beforeprint', onBeforePrint);
      window.removeEventListener('afterprint', onAfterPrint);
    };
  }, []);

  // Default date range filter
  const [dateRange, setDateRange] = useState(() => {
    // IDI-615 D2: the range ends on the dealer's today; it starts on the 1st
    // of the month two months back.
    const end = dealerToday();
    const [y, m] = end.split('-').map(Number);
    const startMonth = new Date(y, m - 1 - 2, 1);
    return {
      start_date: `${startMonth.getFullYear()}-${String(startMonth.getMonth() + 1).padStart(2, '0')}-01`,
      end_date: end,
    };
  });

  // Report switcher (LAT-18) — get all reports in same category
  const sameCategory = useMemo(() => {
    return Object.values(REPORT_DEFINITIONS).filter(r => r.category === reportConfig.category);
  }, [reportConfig.category]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const fetcher = dataSourceMap[reportConfig.dataSource];
      if (!fetcher) throw new Error('Unknown data source');
      const result = await fetcher(dateRange);
      // IDI-141: `jobs` returns a wrapped `{jobs, ...}` envelope (default cap
      // kicks the endpoint into wrapped mode).
      // IDI-216 (item 3, 2026-08-31): `quotes` now ALWAYS returns the
      // enveloped shape too (previously bare-array; this branch relied on
      // that). Invoices still bare-array until its wrapper caps.
      // Explicit branch instead of unconditional unwrapList so:
      //   (a) reader is obvious to a future maintainer,
      //   (b) if invoices' contract changes in an unknown way, the branch
      //       is easy to extend with the right collection key.
      // See debt item: when getInvoices caps, revisit this branch.
      if (reportConfig.dataSource === 'jobs') {
        setRawData(unwrapList(result, 'jobs', 'ReportDetail').list);
      } else if (reportConfig.dataSource === 'quotes') {
        setRawData(unwrapList(result, 'quotes', 'ReportDetail').list);
      } else {
        setRawData(Array.isArray(result) ? result : []);
      }
    } catch (err) {
      setError('Unable to load report data. Please try again.');
      setRawData([]);
    } finally {
      setLoading(false);
    }
  }, [reportConfig.dataSource, dateRange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Active filter chips (LAT-16)
  const activeFilters = useMemo(() => {
    return Object.entries(filters)
      .filter(([, value]) => value && value !== 'all' && value !== '')
      .map(([key, value]) => {
        const filterDef = reportConfig.filters.find(f => f.key === key);
        return { key, value, label: filterDef?.label || key };
      });
  }, [filters, reportConfig.filters]);

  const clearAllFilters = () => {
    setFilters({});
    setCurrentPage(1);
  };

  const removeFilter = (key) => {
    setFilters(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setCurrentPage(1);
  };

  // Process rows
  const processedData = useMemo(() => {
    let data = rawData;

    // IDI-777: From and To decide what the report shows. Each record is placed
    // by its own report date (D2) as the dealer's day (D3), both ends included;
    // undated records stay (D6). Inventory has no range and is untouched.
    const ranged = hasDateRange(reportConfig);
    if (ranged) {
      // IDI-779: the day rides on the row itself (not keyed by object), so a
      // preFilter that returns new rows (the job reports' derived status)
      // keeps the day the range filtered on.
      data = data
        .map(item => ({ item, day: reportDayOf(reportConfig, item) }))
        .filter(({ day }) => inRange(day, dateRange.start_date, dateRange.end_date))
        .map(({ item, day }) => ({ ...item, __reportDay: day || null }));
    }

    // Apply preFilter if defined (e.g., Lost Quotes only shows declined)
    if (reportConfig.preFilter) {
      data = reportConfig.preFilter(data);
    }

    // Apply filters
    Object.entries(filters).forEach(([key, value]) => {
      if (!value || value === 'all') return;
      // IDI-779 D3: a filter marked `match: 'exact'` matches the whole value,
      // so "scheduled" never catches "not_scheduled". Others match by contains.
      const exact = reportConfig.filters?.find(f => f.key === key)?.match === 'exact';
      data = data.filter(item => {
        const itemVal = item[key];
        if (typeof value === 'string' && exact) {
          return String(itemVal || '').toLowerCase() === value.toLowerCase();
        }
        if (typeof value === 'string') {
          return String(itemVal || '').toLowerCase().includes(value.toLowerCase());
        }
        return itemVal === value;
      });
    });

    // Apply computeRow
    const sources = data;
    if (reportConfig.computeRow) {
      data = data.map(reportConfig.computeRow);
    }

    // IDI-777: carry the report day so month headings and the chart read the
    // same date the range filtered on.
    if (ranged) {
      data = data.map((row, i) => ({ ...row, __reportDay: sources[i].__reportDay || null }));
    }

    return data;
  }, [rawData, filters, reportConfig, dateRange]);

  // Compute KPIs
  const kpiValues = useMemo(() => {
    if (reportConfig.computeKpis) {
      return reportConfig.computeKpis(processedData);
    }
    return {};
  }, [processedData, reportConfig]);

  // Sort
  const sortedData = useMemo(() => {
    if (!sortKey) return processedData;
    return [...processedData].sort((a, b) => {
      const aVal = a[sortKey];
      const bVal = b[sortKey];
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
      }
      return sortDir === 'asc'
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });
  }, [processedData, sortKey, sortDir]);

  // Compute effective groupBy from slicer selection
  const slicerToGroupBy = {
    'By Month': 'month',
    'By Job Type': 'job_type',
    'By Salesperson': 'salesperson',
  };
  const effectiveGroupBy = slicerToGroupBy[activeSlicer] || reportConfig.groupBy || 'month';

  // LAT-20: Group data by the active slicer dimension
  const groupedData = useMemo(() => {
    if (!effectiveGroupBy) return null;
    const groups = {};
    processedData.forEach(row => {
      let key;
      if (effectiveGroupBy === 'month' && '__reportDay' in row) {
        // IDI-777: the month of the row's report day, labelled from the key.
        key = row.__reportDay ? monthLabel(monthKeyOf(row.__reportDay), { long: true }) : 'No Date';
      } else if (effectiveGroupBy === 'month') {
        const dateVal = row.created_at || row.createdAt || row.date || row.paid_date;
        key = dateVal ? (parseDate(dateVal) || new Date(dateVal)).toLocaleDateString('en-US', { year: 'numeric', month: 'long' }) : 'No Date';
      } else if (effectiveGroupBy === 'job_type') {
        key = row.job_type || row.jobType || row.type || 'Other';
      } else if (effectiveGroupBy === 'salesperson') {
        key = row.salesperson || row.assigned_to || row.assignedTo || 'Unassigned';
      } else {
        const dateVal = row[effectiveGroupBy];
        key = dateVal ? (parseDate(dateVal) || new Date(dateVal)).toLocaleDateString('en-US', { year: 'numeric', month: 'long' }) : 'No Date';
      }
      if (!groups[key]) groups[key] = [];
      groups[key].push(row);
    });
    return groups;
  }, [processedData, effectiveGroupBy]);

  // Paginate
  const totalPages = Math.ceil(sortedData.length / pageSize);
  const paginatedData = sortedData.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  // IDI-797 D1/D2: the grouped view pages one window of 20 over the rows in
  // heading order. Each heading the page reaches keeps its full group (count
  // and subtotal); only the rows it shows come from the page.
  const pagedGroups = useMemo(() => {
    if (!groupedData) return [];
    const start = (currentPage - 1) * pageSize;
    const end = currentPage * pageSize;
    const out = [];
    let offset = 0;
    Object.entries(groupedData).forEach(([groupLabel, groupRows]) => {
      const from = printAll ? 0 : Math.max(0, start - offset);
      const to = printAll ? groupRows.length : Math.min(groupRows.length, end - offset);
      offset += groupRows.length;
      if (to > from) out.push({ groupLabel, groupRows, pageRows: groupRows.slice(from, to) });
    });
    return out;
  }, [groupedData, currentPage, pageSize, printAll]);
  const sortedRowsShown = printAll ? sortedData : paginatedData;

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setCurrentPage(1);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const handleExportCSV = () => {
    if (sortedData.length === 0) return;
    const headers = reportConfig.columns.map(c => c.label).join(',');
    const rows = sortedData.map(row =>
      reportConfig.columns.map(col => {
        const val = row[col.key];
        if (col.format === 'currency') return val != null ? (val / 100).toFixed(2) : '';
        if (col.format === 'date') return val ? (parseDate(val) || new Date(val)).toLocaleDateString() : '';
        return val ?? '';
      }).join(',')
    ).join('\n');
    const csv = `${headers}\n${rows}`;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${reportConfig.id}_${dealerToday()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // LAT-17: PDF export (uses browser print as PDF)
  const handleExportPDF = () => {
    if (sortedData.length === 0) return;
    window.print();
  };

  const handleReportSwitch = (reportId) => {
    const newConfig = REPORT_DEFINITIONS[reportId];
    if (newConfig) {
      setReportConfig(newConfig);
      setFilters({});
      setSortKey(null);
      setCurrentPage(1);
    }
  };

  // RPT-1 State C: single title-chevron switcher (replaces both prior SelectMenu switchers).
  // Prefer parent-provided allReports; fall back to same-category set.
  const combinedReportsForSwitcher = useMemo(() => {
    if (allReports && allReports.length > 0) return allReports;
    return sameCategory.map((r) => ({ value: r.id, label: r.title }));
  }, [allReports, sameCategory]);

  const handleAnyReportSwitch = (reportId) => {
    if (onSwitchReport) {
      onSwitchReport(reportId);
    } else {
      handleReportSwitch(reportId);
    }
  };

  const hasData = sortedData.length > 0;

  return (
    <div className="space-y-4 max-w-7xl mx-auto">
      {/* Header with back-link above title */}
      <PageHeader
        title={
          combinedReportsForSwitcher.length > 0 ? (
            <ReportTitleSwitcher
              current={reportConfig}
              allReports={combinedReportsForSwitcher}
              onSwitch={handleAnyReportSwitch}
            />
          ) : (
            reportConfig.title
          )
        }
        backTo="/reports"
        backLabel="Back to Reports"
        actions={<DownloadDropdown hasData={hasData} onCSV={handleExportCSV} onPDF={handleExportPDF} />}
      />
      {/* Filters */}
      <div className="card p-4">
        <div className="flex flex-wrap items-end gap-3">
          {reportConfig.filters.map(filter => {
            if (filter.type === 'date_range') {
              return (
                <div key={filter.key} className="flex items-end gap-2">
                  <div>
                    <label htmlFor={`filter-${filter.key}-from`} className="label">From</label>
                    <DatePicker
                      id={`filter-${filter.key}-from`}
                      value={dateRange.start_date}
                      onChange={val => { setDateRange(prev => ({ ...prev, start_date: val })); setCurrentPage(1); }}
                    />
                  </div>
                  <div>
                    <label htmlFor={`filter-${filter.key}-to`} className="label">To</label>
                    <DatePicker
                      id={`filter-${filter.key}-to`}
                      value={dateRange.end_date}
                      onChange={val => { setDateRange(prev => ({ ...prev, end_date: val })); setCurrentPage(1); }}
                    />
                  </div>
                </div>
              );
            }
            if (filter.type === 'select') {
              return (
                <div key={filter.key}>
                  <label htmlFor={`filter-${filter.key}`} className="label">{filter.label}</label>
                  <SelectMenu
                    id={`filter-${filter.key}`}
                    value={filters[filter.key] || 'all'}
                    onChange={(val) => handleFilterChange(filter.key, val)}
                    options={filter.options.map(opt => ({
                      value: opt,
                      label: opt === 'all' ? 'All' : opt.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
                    }))}
                  />
                </div>
              );
            }
            if (filter.type === 'text') {
              return (
                <div key={filter.key}>
                  <label htmlFor={`filter-${filter.key}`} className="label">{filter.label}</label>
                  <input
                    id={`filter-${filter.key}`}
                    type="text"
                    className="input"
                    value={filters[filter.key] || ''}
                    onChange={e => handleFilterChange(filter.key, e.target.value)}
                  />
                </div>
              );
            }
            return null;
          })}
        </div>

        {/* LAT-16: Active filter chips */}
        {activeFilters.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mt-3 pt-3" style={{ borderTop: '1px solid var(--color-border)' }}>
            {activeFilters.map(f => (
              <span
                key={f.key}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium"
                style={{ backgroundColor: 'var(--neutral-100)', color: 'var(--color-text-primary)' }}
              >
                {f.label}: {f.value}
                <button
                  onClick={() => removeFilter(f.key)}
                  className="ml-1 hover:opacity-70"
                  aria-label={`Remove ${f.label} filter`}
                >
                  ×
                </button>
              </span>
            ))}
            <button
              className="text-xs font-medium px-2 py-1 rounded focus-ring"
              style={{ color: 'var(--color-action-primary)' }}
              onClick={clearAllFilters}
            >
              Clear All
            </button>
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <Alert variant="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* IDI-333: everything from here down is the printable report —
          KPIs, chart, and the data table. Filters/pagination controls above
          and below this wrapper are deliberately excluded from print (they
          have no meaning on a static export). `.rpt-doc` carries this
          surface's own geometry; `.print-root` (print-root.css) carries the
          shared hide-everything-else mechanism — see that file's docblock. */}
      <div className="rpt-doc print-root">

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {reportConfig.kpis.map(kpi => (
          <KpiCard
            key={kpi.key}
            label={kpi.label}
            value={loading ? '—' : kpiValues[kpi.key]}
            format={kpi.format}
          />
        ))}
      </div>

      {/* Slicer tabs — REPORTS_DETAIL_SPEC §Div-3 */}
      {REPORT_SLICERS[reportConfig.id] && REPORT_SLICERS[reportConfig.id].length > 0 && (
        <div className="card" style={{ padding: '0 16px' }}>
          <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--color-border)' }}>
            {REPORT_SLICERS[reportConfig.id].map(slicer => (
              <button
                key={slicer}
                onClick={() => { setActiveSlicer(slicer); setCurrentPage(1); }}
                style={{
                  border: 'none',
                  background: 'transparent',
                  padding: '12px 16px',
                  fontSize: 13,
                  fontWeight: activeSlicer === slicer ? 600 : 400,
                  color: activeSlicer === slicer ? 'var(--color-action-primary)' : 'var(--color-text-muted)',
                  borderBottom: activeSlicer === slicer ? '2px solid var(--color-action-primary)' : '2px solid transparent',
                  cursor: 'pointer',
                  marginBottom: -1,
                }}
              >
                {slicer}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Chart */}
      {reportConfig.chartType && (
        <div className="card p-4">
          <ReportChart
            chartType={reportConfig.chartType}
            chartConfig={{
              ...reportConfig.chartConfig,
              // IDI-797 D6: a status chart (Job Status) reads by status on
              // every tab; every other chart follows the tab. Do not read the
              // definition's groupBy for all charts (Margin Analysis says
              // `type` and opens by month).
              groupBy: reportConfig.chartConfig?.groupBy === 'status' ? 'status' : effectiveGroupBy,
            }}
            data={processedData}
            range={hasDateRange(reportConfig) ? dateRange : undefined}
            loading={loading}
            onDataPointClick={(dataPoint) => {
              if (dataPoint?.name) {
                setFilters(prev => ({ ...prev, [effectiveGroupBy]: dataPoint.name }));
              }
            }}
          />
        </div>
      )}

      {/* Data table */}
      <div className="card overflow-hidden" data-density="compact">
        {loading ? (
          <div className="p-8 text-center" style={{ color: 'var(--color-text-secondary)' }}>
            <div className="animate-spin h-6 w-6 border-2 rounded-full mx-auto mb-2" style={{ borderColor: 'var(--neutral-300)', borderTopColor: 'var(--color-action-primary)' }} />
            Loading report data...
          </div>
        ) : paginatedData.length === 0 ? (
          /* LAT-22: Empty/filtered state */
          <div className="p-8 text-center" style={{ color: 'var(--color-text-secondary)' }}>
            <p className="font-medium mb-1" style={{ color: 'var(--color-text-primary)' }}>
              {activeFilters.length > 0 ? 'No results match your filters' : 'No data found'}
            </p>
            <p className="text-sm">
              {activeFilters.length > 0
                ? 'Try adjusting or clearing your filters.'
                : 'Try adjusting your date range.'}
            </p>
            {activeFilters.length > 0 && (
              <button
                className="btn btn-secondary btn-sm mt-3"
                onClick={clearAllFilters}
              >
                Clear Filters
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr style={{ backgroundColor: 'var(--table-header-bg)', borderBottom: '1px solid var(--color-border)' }}>
                    {reportConfig.columns.map(col => (
                      <th
                        key={col.key}
                        className={`py-2 px-3 text-xs uppercase tracking-wider font-medium ${col.align === 'right' ? 'text-right' : 'text-left'} ${col.sortable ? 'cursor-pointer select-none' : ''}`}
                        style={{ color: 'var(--table-header-text)' }}
                        onClick={col.sortable ? () => handleSort(col.key) : undefined}
                      >
                        {col.label}
                        {sortKey === col.key && (
                          <span className="ml-1">{sortDir === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {/* LAT-20: Grouped rendering with subtotals */}
                  {groupedData && !sortKey ? (
                    pagedGroups.map(({ groupLabel, groupRows, pageRows }) => {
                      const groupTotal = groupRows.reduce((sum, r) => sum + (r.amount || r.total || 0), 0);
                      return (
                        <React.Fragment key={groupLabel}>
                          <tr style={{ backgroundColor: 'var(--color-surface-sunken)' }}>
                            <td
                              colSpan={reportConfig.columns.length}
                              className="py-2 px-3 text-xs font-semibold uppercase"
                              style={{ color: 'var(--color-text-secondary)' }}
                            >
                              {groupLabel} ({groupRows.length})
                              {groupTotal > 0 && (
                                <span className="ml-2 font-normal">
                                  — {formatValue(groupTotal, 'currency')}
                                </span>
                              )}
                            </td>
                          </tr>
                          {pageRows.map((row, idx) => (
                            <tr
                              key={row.id || idx}
                              style={{ borderBottom: '1px solid var(--color-border)' }}
                            >
                              {reportConfig.columns.map(col => (
                                <td
                                  key={col.key}
                                  className={`py-2 px-3 ${col.align === 'right' ? 'text-right' : 'text-left'}`}
                                >
                                  {col.format === 'bar'
                                    ? formatValue(row.margin_pct ?? row[col.key], 'bar')
                                    : formatValue(row[col.key], col.format)
                                  }
                                </td>
                              ))}
                            </tr>
                          ))}
                        </React.Fragment>
                      );
                    })
                  ) : (
                    sortedRowsShown.map((row, idx) => (
                      <tr
                        key={row.id || idx}
                        style={{ borderBottom: '1px solid var(--color-border)' }}
                      >
                        {reportConfig.columns.map(col => (
                          <td
                            key={col.key}
                            className={`py-2 px-3 ${col.align === 'right' ? 'text-right' : 'text-left'}`}
                          >
                            {col.format === 'bar'
                              ? formatValue(row.margin_pct ?? row[col.key], 'bar')
                              : formatValue(row[col.key], col.format)
                            }
                          </td>
                        ))}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination — excluded from print via .rpt-pagination (see
                ReportDetail.print.css); it's inside .rpt-doc/.print-root so
                it would otherwise print along with the table. */}
            {totalPages > 1 && (
              <div className="rpt-pagination flex items-center justify-between px-4 py-3" style={{ borderTop: '1px solid var(--color-border)' }}>
                <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                  Showing {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, sortedData.length)} of {sortedData.length}
                </p>
                <div className="flex gap-1">
                  <button
                    className="btn btn-ghost btn-sm"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(p => p - 1)}
                  >
                    ← Prev
                  </button>
                  <button
                    className="btn btn-ghost btn-sm"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(p => p + 1)}
                  >
                    Next →
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
      </div>
    </div>
  );
}
